import { useSearchParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { Container } from '@radix-ui/themes'

import { Salenote } from '../interfaces/Salenote'
import useSalenoteFetcher from '../hooks/useSalenoteFetcher'
import { insertAfterFirstToken } from '../utils/utils'
import calcPlateDv from '../utils/calcPlateDv'
import { useAuth } from '../hooks/useAuth'
import { Delegate } from '../interfaces/OrganizationDto'
import { useConsignmentFetcher } from '../hooks/useConsignmentFetcher'
import Consignment from '../interfaces/Consignment'
import useOrganizationFetcher from '../hooks/useOrganizationFetcher'
import UserData from '../interfaces/UserData'
import { Organization } from '../interfaces/Organization'

import { SalenoteContractTemplate } from './SalenoteContractTemplate'
import { RadioGroup, RadioItem } from './RadioGroup'
import { ButtonAction } from './Buttons'
import DisplayError from './DisplayError'

const SalenoteContract: React.FC = () => {
  const [searchParams] = useSearchParams()
  const { errorSalenote, fetchSalenote, fetchSalenoteTradeIn } = useSalenoteFetcher()
  const { errorConsignment, fetchConsignmentVehicle } = useConsignmentFetcher()
  const { fetchOrganization } = useOrganizationFetcher()

  const [errorMessage, setErrorMessage] = useState<string>('')
  const [salenote, setSalenote] = useState<Salenote>()
  const [salenoteTradeIn, setSalenoteTradeIn] = useState<Salenote>()
  // const [vehPlate, setVehPlate] = useState<string>("");
  const { userData: authUserData } = useAuth()
  const [localUserData, setLocalUserData] = useState<UserData | null>(null)

  const [show, setShow] = useState<boolean>(false)
  const id = searchParams.get('id')
  const [printDisabled, setPrintDisabled] = useState<boolean>(true)
  // Muestra la parte en donde el usuario selecciona "automotora" o cliente
  const [showChooseSeller, setShowChooseSeller] = useState<boolean>(false)

  const [delegates, setDelegates] = useState<Delegate[]>([])
  const [showRepresentatives, setShowRepresentatives] = useState<boolean>(false)
  const [sellerName, setSellerName] = useState<string>('')
  const [sellerRut, setSellerRut] = useState<string>('')
  const [sellerAddr, setSellerAddr] = useState<string>('')
  const [personeria1, setPersoneria1] = useState<string>('')
  const [personeria2, setPersoneria2] = useState<string>('')
  const [representadoPorSpan, setRepresentadoPorSpan] = useState(<span>,</span>)
  const [representado2PorSpan, setRepresentado2PorSpan] = useState(<span></span>)
  const [vehPlateSpan, setVehPlateSpan] = useState(<span></span>)

  useEffect(() => {
    if (authUserData) {
      setLocalUserData(authUserData)
      fetchDataAsync()
    }
  }, [id, authUserData])

  const fetchDataAsync = async () => {
    if (!id) {
      return // Exit early if `id` is undefined
    }

    const salenote = await fetchSalenote(id)

    if (errorSalenote) {
      setErrorMessage(errorSalenote.message)

      return
    }

    if (!salenote) {
      setErrorMessage(`Error recuperando la Nota de venta`)

      return
    }

    // Fetch fresh organization data to ensure we have latest delegates
    // This fixes issues where session data is stale
    let currentUserData = authUserData

    if (salenote.organization && authUserData) {
      const freshOrg = await fetchOrganization(salenote.organization)

      if (freshOrg) {
        // Create a merged UserData object with the fresh organization
        currentUserData = {
          ...authUserData,
          organization: {
            ...authUserData.organization, // Keep pictures/logo which might be missing in Dto
            ...freshOrg,
          } as Organization,
        }
        setLocalUserData(currentUserData)
      }
    }

    const plate = salenote?.body.vehicle.plate ?? ''
    const calcPlateDvResult = calcPlateDv(plate)
    let auxPlate

    if (calcPlateDvResult.ok) {
      auxPlate = calcPlateDvResult.plate
    } else {
      setErrorMessage(calcPlateDvResult.msg)
      auxPlate = plate
    }
    //  setVehPlate(auxPlate);
    setVehPlateSpan(<span className="font-bold underline">{auxPlate}</span>)

    // Ahora buscamos la consignacion, o la compra, para saber
    // quien es el dueño del vehiculo
    const vehState = salenote?.body.vehicle.state

    if (!vehState) {
      setErrorMessage('No está definido el estado de pertenencia del vehículo.')

      return
    }

    if (vehState !== 'STATE.VEHICLE.PURCHASED') {
      // consignacion
      const consig = await fetchConsignmentVehicle(salenote.vehicle)

      if (errorConsignment) {
        setErrorMessage(errorConsignment.message)

        return
      }

      if (!consig) {
        setErrorMessage(`No se encuentra ninguna Consignación archivada para este Vehículo`)

        return
      }
      setPrintDisabled(false)
      setSellerConsignment(consig)
      setShow(true)
    } else {
      // Busquemos si este vehiculo esta como parte de pago de alguna nota de venta
      // Por patente
      const salenoteTradeIn = await fetchSalenoteTradeIn(plate)

      if (salenoteTradeIn) {
        // Es un vehículo en parte de pago (Trade In)
        setSalenoteTradeIn(salenoteTradeIn)
        setShowChooseSeller(true)
        setShow(false)
      } else {
        // Es una compra directa (Stock propio de la automotora)
        // No hay "Cliente" anterior para firmar, el vendedor es la Automotora.
        const msg = checkDelegates(currentUserData)

        if (msg) {
          setErrorMessage(msg)
          setShow(false)
        } else {
          putValuesSellerOrg(currentUserData)
          setShow(true)
        }
      }
    }
    setSalenote(salenote)
  }

  const setSellerConsignment = (consignment: Consignment) => {
    // El vendedor lo obtenemos de la consignacion.
    setSellerName(`${consignment?.body.client.firstname} ${consignment?.body.client.lastname}`)
    setSellerRut(consignment?.body.client.nin)

    const c1 = consignment?.body.client.address
    const c2 = (consignment?.body.client.streetNumber ?? '').toString()
    const c3 = consignment?.body.client.addressComponents ?? ''
    const fullAddress = insertAfterFirstToken(c1, c2, c3)

    setSellerAddr(fullAddress)
  }

  const checkDelegates = (uData: UserData | null) => {
    // Leemos la organization para obtener los delegados.
    const organizationObj = uData?.organization

    if (!organizationObj) {
      return 'Error reading organization'
    }
    let sellerDelegates: Delegate[]

    // Y necesitamos los datos de o de los representantes
    const delegates: Delegate[] = uData?.organization?.delegates ?? []

    if (delegates) {
      sellerDelegates = delegates.filter((d) => !d.disabledAt)

      if (sellerDelegates.length >= 1) {
        if (isDelegatesOk(delegates)) {
          setPrintDisabled(false)
        }
      } else {
        // If 0 active delegates
        return "No existen representantes definidos (Activos). Por favor, usa la opción 'Automotora' para agregarlos."
      }
    } else {
      return "No existen representantes definidos. Por favor, usa la opción 'Automotora' para agregarlos."
    }

    if (!organizationObj.articles) {
      return "No existen datos de la Escritura. Por favor, usa la opción 'Automotora' para agregarlos."
    }

    return null
  }

  // True si los delegados estan OK, en el sentido de que las
  // variables están llenas (firsName, lastName, nin)
  const isDelegatesOk = (delegates: Delegate[]) => {
    let sw = true
    delegates.forEach((d) => {
      if (!(d.firstname && d.lastname && d.nin)) {
        sw = false
      }
    })

    return sw
  }

  const handlePrintClick = async () => {
    if (printDisabled) return

    window.print()
  }

  let sellerClientName = 'Cliente: '

  if (showChooseSeller && salenoteTradeIn) {
    sellerClientName += `${salenoteTradeIn.body.client.firstname} ${salenoteTradeIn.body.client.lastname}`
  }
  const stockCollection: RadioItem[] = [{ label: 'Automotora' }, { label: sellerClientName }]
  const [sellerIdx, setSellerIdx] = useState<number>(-1)
  const stockFilter = (idx: number) => {
    if (idx === 1) {
      putValuesSellerCli()
      setShow(true)
    } else {
      const msg = checkDelegates(localUserData)

      if (msg) {
        setErrorMessage(msg)
        setShow(false)
      } else {
        putValuesSellerOrg(localUserData)
        setShow(true)
      }
    }

    setSellerIdx(idx)
  }

  // Setea los valores con los datos de la automotora (organization)
  const putValuesSellerOrg = (uData: UserData | null) => {
    setShowRepresentatives(true)
    const delegates: Delegate[] = uData?.organization?.delegates ?? []
    setDelegates(delegates)

    // el "vendedor" es la organizacion
    setSellerName(uData?.organization?.legalName ?? '')
    setSellerRut(uData?.organization?.nin ?? '')

    const c1 = uData?.organization?.address
    const c2 = (uData?.organization?.streetNumber ?? '').toString()
    const c3 = uData?.organization?.addressComponents ?? ''
    const fullAddress = insertAfterFirstToken(c1, c2, c3)
    setSellerAddr(fullAddress)

    if (delegates.length > 0) {
      setPersoneria1(`${delegates[0].firstname} ${delegates[0].lastname}`)
      setRepresentadoPorSpan(
        <span>
          , representado por{' '}
          <span className="font-bold underline">
            {delegates[0].firstname} {delegates[0].lastname} RUT {delegates[0].nin}
          </span>
        </span>
      )
    }

    if (delegates.length >= 2) {
      setPersoneria2(`${delegates[1].firstname} ${delegates[1].lastname}`)
      setRepresentado2PorSpan(
        <span className="font-bold underline">
          {' '}
          y {personeria2} RUT {delegates[1].nin}
        </span>
      )
    }

    setPrintDisabled(false)
  }
  const putValuesSellerCli = () => {
    setShowRepresentatives(false)
    setRepresentadoPorSpan(<span>,</span>)
    setRepresentado2PorSpan(<span></span>)

    if (salenoteTradeIn) {
      setPrintDisabled(false)
      setErrorMessage('')
      // los datos del Vendedor vienen en salenoteTradeIn
      setSellerName(`${salenoteTradeIn.body.client.firstname} ${salenoteTradeIn.body.client.lastname}`)
      setSellerRut(salenoteTradeIn.body.client.nin)
      const c1 = salenoteTradeIn.body.client.address
      const c2 = (salenoteTradeIn.body.client.streetNumber ?? '').toString()
      const c3 = salenoteTradeIn.body.client.addressComponents ?? ''
      const fullAddress = insertAfterFirstToken(c1, c2, c3)
      setSellerAddr(fullAddress)
    }
  }

  return (
    <Container size="3" m="4">
      {errorMessage && (
        <div className="text-xl">
          <DisplayError msg={errorMessage} />
        </div>
      )}
      {showChooseSeller && (
        <div className="print:hidden text-xl">
          <RadioGroup
            collection={stockCollection}
            name={'seller'}
            direction={'horizontal'}
            checked={sellerIdx}
            callback={(idx) => stockFilter(idx)}
            label=""
            className="flex items-center space-x-2 mr-10 mb-4"
          />
        </div>
      )}
      {show && salenote && localUserData && (
        <div>
          <SalenoteContractTemplate
            salenote={salenote}
            userData={localUserData}
            sellerName={sellerName}
            sellerRut={sellerRut}
            sellerAddr={sellerAddr}
            delegates={delegates}
            showRepresentatives={showRepresentatives}
            personeria1={personeria1}
            personeria2={personeria2}
            representadoPorSpan={representadoPorSpan}
            representado2PorSpan={representado2PorSpan}
            vehPlateSpan={vehPlateSpan}
          />

          <div className="mt-8 print:hidden">
            <ButtonPrintEx handlePrintClick={handlePrintClick} disabled={printDisabled} />
          </div>
        </div>
      )}
      <div className="flex justify-center print:hidden mt-2 mb-6">
        <ButtonAction label="Cerrar" handleButtonClick={() => window.close()} />
      </div>
    </Container>
  )
}
const ButtonPrintEx: React.FC<{
  handlePrintClick: () => void
  disabled: boolean
}> = ({ handlePrintClick, disabled }) => {
  return (
    <div className="flex flex-col items-center print:hidden">
      <ButtonAction label={'\u{1F5B6} Imprimir'} handleButtonClick={handlePrintClick} disabled={disabled} />
    </div>
  )
}

export default SalenoteContract
